import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import _ from 'lodash'
import type { TpaCommonsSiteConfig } from 'feature-tpa-commons'
import { name as tpaCommons } from 'feature-tpa-commons'
import type { ILogger, IPropsStore } from '@wix/thunderbolt-symbols'
import { LoggerSymbol, PageFeatureConfigSymbol, Props, SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import type { ITpaRenderManager, ITpaLoadMeasure, TpaPageConfig } from './types'
import { getFullId } from '@wix/thunderbolt-commons'
import { name, TpaRenderManagerSymbol } from './symbols'

const getTpaCompData = (tpaPageConfig: TpaPageConfig, compId: string) => {
	const { widgets } = tpaPageConfig
	const tpaCompData = widgets[compId] || widgets[getFullId(compId)]
	const { widgetId, appDefinitionId } = tpaCompData || {}
	return { widgetId, appDefinitionId }
}

const getIsWixTPA = (widgetId: string, widgetsClientSpecMapData: TpaCommonsSiteConfig['widgetsClientSpecMapData']) => {
	return widgetId && Boolean(widgetsClientSpecMapData[widgetId]?.isWixTPA)
}

export const TpaLoadMeasure = withDependencies(
	[
		named(SiteFeatureConfigSymbol, tpaCommons),
		named(PageFeatureConfigSymbol, name),
		Props,
		LoggerSymbol,
		optional(TpaRenderManagerSymbol),
	],
	(
		{ widgetsClientSpecMapData }: TpaCommonsSiteConfig,
		tpaPageConfig: TpaPageConfig,
		props: IPropsStore,
		logger: ILogger,
		tpaRenderManager?: ITpaRenderManager
	): ITpaLoadMeasure => {
		const loadDurationTrackers: { [compId: string]: { src: string; reportLoadFinished: () => void } } = {}
		const isWixTpaFinishedRenderingMap: { [compId: string]: boolean } = {}
		return {
			reportTpaLoadStart: (compId, newSrc) => {
				const { widgetId, appDefinitionId } = getTpaCompData(tpaPageConfig, compId)

				const isWixTpa = getIsWixTPA(widgetId, widgetsClientSpecMapData)
				if (!isWixTpa) {
					return
				}

				if (tpaRenderManager) {
					isWixTpaFinishedRenderingMap[compId] = false
				}

				const shouldTrack = newSrc && props.get(compId)?.src !== newSrc
				if (!shouldTrack) {
					// only track wix TPAs with valid src that has changed
					return
				}

				const TPA_LOAD_EVENT = 'tpa_iframe_load'
				const loadStart = Date.now()
				logger.interactionStarted(TPA_LOAD_EVENT, {
					customParams: {
						appDefinitionId,
						widgetId,
					},
				})
				loadDurationTrackers[compId] = {
					src: newSrc,
					reportLoadFinished: _.once(() =>
						logger.interactionEnded(TPA_LOAD_EVENT, {
							customParams: {
								appDefinitionId,
								widgetId,
								duration: Date.now() - loadStart,
							},
						})
					),
				}
			},
			reportTpaLoadEnd: (compId) => {
				if (tpaRenderManager) {
					const { widgetId } = getTpaCompData(tpaPageConfig, compId)
					const isWixTpa = getIsWixTPA(widgetId, widgetsClientSpecMapData)
					if (isWixTpa) {
						isWixTpaFinishedRenderingMap[compId] = true
						if (Object.values(isWixTpaFinishedRenderingMap).every(Boolean)) {
							tpaRenderManager.notifyAllWixTpasRendered()
						}
					}
				}
				const loadEndReporter = loadDurationTrackers[compId]
				if (loadEndReporter?.src && props.get(compId)?.src === loadEndReporter.src) {
					// dont report stale src
					loadEndReporter.reportLoadFinished()
				}
			},
		}
	}
)
